import { createContext, useContext, useCallback, useState, useMemo, type ReactNode } from "react";
import { toast } from "sonner";

type NotificationType = "success" | "error" | "info" | "warning";

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  timestamp: number;
  read: boolean;
}

// Split context for better performance - actions rarely change
interface NotificationActionsContextType {
  notify: (type: NotificationType, title: string, message?: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotification: (id: string) => void;
  clearAll: () => void;
}

interface NotificationStateContextType {
  notifications: Notification[];
  unreadCount: number;
}

const NotificationActionsContext = createContext<NotificationActionsContextType | null>(null);
const NotificationStateContext = createContext<NotificationStateContextType | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
  maxNotifications?: number;
}

export function NotificationProvider({ children, maxNotifications = 50 }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Memoize actions since they don't depend on state
  const actions = useMemo<NotificationActionsContextType>(() => ({
    notify: (type: NotificationType, title: string, message?: string) => {
      const id = crypto.randomUUID();
      const notification: Notification = {
        id,
        type,
        title,
        message,
        timestamp: Date.now(),
        read: false,
      };

      setNotifications((prev) => [notification, ...prev].slice(0, maxNotifications));

      // Show toast
      const toastFn = type === "success" ? toast.success
        : type === "error" ? toast.error
        : type === "warning" ? toast.warning
        : toast.info;
      toastFn(title, { description: message });
    },
    markAsRead: (id: string) => {
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
    },
    markAllAsRead: () => {
      setNotifications((prev) => prev.map((n) => (n.read ? n : { ...n, read: true })));
    },
    clearNotification: (id: string) => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    },
    clearAll: () => {
      setNotifications([]);
    },
  }), [maxNotifications]);

  // Memoize unreadCount computation
  const unreadCount = useMemo(
    () => notifications.reduce((count, n) => count + (n.read ? 0 : 1), 0),
    [notifications]
  );

  // Memoize state context value
  const stateValue = useMemo<NotificationStateContextType>(
    () => ({ notifications, unreadCount }),
    [notifications, unreadCount]
  );

  return (
    <NotificationActionsContext.Provider value={actions}>
      <NotificationStateContext.Provider value={stateValue}>
        {children}
      </NotificationStateContext.Provider>
    </NotificationActionsContext.Provider>
  );
}

// Hook for actions only - stable reference, won't cause re-renders
export function useNotificationActions() {
  const context = useContext(NotificationActionsContext);
  if (!context) {
    throw new Error("useNotificationActions must be used within a NotificationProvider");
  }
  return context;
}

// Hook for state only - re-renders when notifications change
export function useNotificationState() {
  const context = useContext(NotificationStateContext);
  if (!context) {
    throw new Error("useNotificationState must be used within a NotificationProvider");
  }
  return context;
}

// Selector hook - only re-renders when selected value changes
export function useUnreadCount(): number {
  return useNotificationState().unreadCount;
}

// Combined hook for backwards compatibility
export function useNotifications() {
  const actions = useNotificationActions();
  const state = useNotificationState();
  return { ...state, ...actions };
}

export type { Notification, NotificationType };
